import { useState } from 'react';
import StatusBar from './StatusBar.jsx';
import BottomTabBar from './BottomTabBar.jsx';
import ScreenContainer from './ScreenContainer.jsx';
import LivePill from '../live/LivePill.jsx';
import LiveTrackMap from '../live/LiveTrackMap.jsx';
import useLiveSession from '../../hooks/useLiveSession.js';

/**
 * AppShell — the outermost layout container.
 *
 * Component hierarchy (bottom-up):
 *
 *   <AppProvider>                              (state)
 *     └── <AppShell>                            (this file)
 *           ├── <StatusBar />                   (top, cosmetic on web)
 *           ├── <ScreenContainer>               (the active screen)
 *           │       └── one of: News | Results | Oracle
 *           ├── <LivePill />                     (floats above the bar, live only)
 *           ├── <LiveTrackMap />                 (full-frame overlay when opened)
 *           └── <BottomTabBar />                (primary nav)
 *
 * The shell is a single flex column. Its child screens own their own
 * scroll containers — never let two scroll contexts stack inside the
 * same column, the sticky headers break.
 *
 * The live layer is owned here, not in a screen, because it is orthogonal to
 * the active tab: a session can be live regardless of what you're reading. The
 * pill self-hides unless useLiveSession reports a running session, so the shell
 * looks identical to before whenever nothing is on track. The `relative` on the
 * root makes it the positioning context for the pill and the overlay.
 *
 * @param {object} props
 * @param {Object<string, JSX.Element>} props.screens
 *   Mapping from tab id ('news' | 'results' | 'oracle') to the screen
 *   instance to render when that tab is active. Decoupling routing from
 *   layout keeps both pieces independently testable.
 */
export default function AppShell({ screens }) {
  const { status, session } = useLiveSession();
  const [mapOpen, setMapOpen] = useState(false);

  const isLive = status === 'live' || status === 'locked';
  // If a session ends while the map is open, drop back to the shell.
  const showMap = mapOpen && isLive;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-bg-base">
      <StatusBar />
      <ScreenContainer screens={screens} />
      {!showMap && <LivePill status={status} session={session} onOpen={() => setMapOpen(true)} />}
      {showMap && <LiveTrackMap status={status} session={session} onClose={() => setMapOpen(false)} />}
      <BottomTabBar />
    </div>
  );
}
